import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { formatDateUK } from '../utils/dateUtils';
import {
  handleApiError,
  showLoadingToast,
  updateToastSuccess,
  updateToastError,
} from '../utils/errorHandler';

const Artworks = () => {
  const [artworks, setArtworks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    customerId: '',
    fileUrl: '',
    fileName: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    fileUrl: '',
    fileName: '',
  });
  const [imageLoadingStates, setImageLoadingStates] = useState({});

  useEffect(() => {
    fetchCustomers();
    fetchAllArtworks();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchArtworks(selectedCustomer);
    } else {
      fetchAllArtworks();
    }
  }, [selectedCustomer]);

  const fetchCustomers = async () => {
    try {
      setCustomersLoading(true);
      const response = await api.get('/customers');
      setCustomers(response.data || []);
    } catch (error) {
      handleApiError(error, 'Failed to load customers');
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
      setLoading(false);
    }
  };

  const fetchAllArtworks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/artworks');
      setArtworks(response.data.data || []);
    } catch (error) {
      handleApiError(error, 'Failed to load artworks');
      setArtworks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtworks = async customerId => {
    setLoading(true);
    try {
      const response = await api.get(`/artworks/customer/${customerId}`);
      setArtworks(response.data.data || []);
    } catch (error) {
      handleApiError(error, 'Failed to load artworks for this customer');
      setArtworks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewArtwork = async artwork => {
    try {
      if (artwork.fileUrl) {
        window.open(artwork.fileUrl, '_blank');
      } else {
        toast.error('No file URL available for this artwork');
      }
    } catch (error) {
      console.error('Error opening artwork:', error);
      toast.error('Failed to open artwork');
    }
  };

  const handleImageLoad = artworkId => {
    setImageLoadingStates(prev => ({
      ...prev,
      [artworkId]: 'loaded',
    }));
  };

  const handleImageError = artworkId => {
    setImageLoadingStates(prev => ({
      ...prev,
      [artworkId]: 'error',
    }));
  };

  const isImageType = url => {
    if (!url) return false;
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i;
    return imageExtensions.test(url);
  };

  const handleEditClick = artwork => {
    setEditingArtwork(artwork);
    setEditForm({
      name: artwork.name,
      description: artwork.description || '',
      fileUrl: artwork.fileUrl || '',
      fileName: artwork.fileName || '',
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async e => {
    e.preventDefault();

    if (!editForm.name.trim()) {
      toast.error('Artwork name is required');
      return;
    }

    const toastId = showLoadingToast('Updating artwork...');

    const updateData = {
      name: editForm.name.trim(),
      description: editForm.description.trim(),
      fileUrl: editForm.fileUrl.trim(),
      fileName: editForm.fileName.trim(),
    };

    try {
      await api.put(`/artworks/${editingArtwork._id}`, updateData);

      updateToastSuccess(toastId, 'Artwork updated successfully');
      setShowEditModal(false);
      setEditingArtwork(null);
      setEditForm({ name: '', description: '', fileUrl: '', fileName: '' });

      if (selectedCustomer) {
        fetchArtworks(selectedCustomer);
      } else {
        fetchAllArtworks();
      }
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to update artwork', false);
      updateToastError(toastId, errorMessage);
    }
  };

  const handleUpload = async e => {
    e.preventDefault();

    if (!uploadForm.name.trim()) {
      toast.error('Artwork name is required');
      return;
    }

    if (!uploadForm.customerId) {
      toast.error('Please select a customer');
      return;
    }

    const toastId = showLoadingToast('Uploading artwork...');

    const artworkData = {
      name: uploadForm.name.trim(),
      description: uploadForm.description.trim(),
      customerId: uploadForm.customerId,
      fileUrl: uploadForm.fileUrl.trim(),
      fileName: uploadForm.fileName.trim(),
    };

    try {
      await api.post('/artworks', artworkData);

      updateToastSuccess(toastId, 'Artwork created successfully');
      setShowUploadModal(false);
      setUploadForm({ name: '', description: '', customerId: '', fileUrl: '', fileName: '' });

      if (selectedCustomer) {
        fetchArtworks(selectedCustomer);
      } else {
        fetchAllArtworks();
      }
    } catch (error) {
      const errorMessage = handleApiError(error, 'Failed to upload artwork', false);
      updateToastError(toastId, errorMessage);
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Artwork Management</h1>
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add Artwork
            </button>
          </div>
        </div>

        {/* Customer Filter */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Customer</label>
          <select
            value={selectedCustomer}
            onChange={e => setSelectedCustomer(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 w-full max-w-md"
          >
            <option value="">All Customers</option>
            {customers && customers.length > 0 ? (
              customers.map(customer => (
                <option key={customer._id} value={customer._id}>
                  {customer.name} - {customer.companyName}
                </option>
              ))
            ) : customersLoading ? (
              <option disabled>Loading customers...</option>
            ) : (
              <option disabled>No customers found</option>
            )}
          </select>
        </div>

        {/* Artworks Grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {artworks.map(artwork => (
              <div
                key={artwork._id}
                className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transform hover:-translate-y-1 transition-all duration-200"
              >
                {/* Image Preview */}
                {artwork.fileUrl && (
                  <div className="relative h-48 bg-gray-100 group">
                    {isImageType(artwork.fileUrl) ? (
                      <>
                        {/* Loading state */}
                        {imageLoadingStates[artwork._id] !== 'loaded' &&
                          imageLoadingStates[artwork._id] !== 'error' && (
                            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            </div>
                          )}

                        {/* Image */}
                        <img
                          src={artwork.fileUrl}
                          alt={artwork.name}
                          className={`w-full h-full object-cover cursor-pointer transition-opacity duration-200 hover:opacity-90 ${
                            imageLoadingStates[artwork._id] === 'loaded'
                              ? 'opacity-100'
                              : 'opacity-0'
                          }`}
                          onClick={() => handleViewArtwork(artwork)}
                          onLoad={() => handleImageLoad(artwork._id)}
                          onError={() => handleImageError(artwork._id)}
                        />

                        {/* Error state for images */}
                        {imageLoadingStates[artwork._id] === 'error' && (
                          <div
                            className="absolute inset-0 bg-gray-100 flex items-center justify-center text-gray-400 cursor-pointer hover:bg-gray-200 transition-colors"
                            onClick={() => handleViewArtwork(artwork)}
                          >
                            <div className="text-center">
                              <svg
                                className="mx-auto h-12 w-12 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              <p className="text-sm mt-2">Image not available</p>
                              <p className="text-xs text-blue-600 hover:text-blue-800">
                                Click to view original
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Non-image file preview */
                      <div
                        className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center text-blue-600 cursor-pointer hover:from-blue-100 hover:to-blue-200 transition-colors"
                        onClick={() => handleViewArtwork(artwork)}
                      >
                        <div className="text-center">
                          <svg
                            className="mx-auto h-12 w-12 text-blue-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <p className="text-sm mt-2 font-medium">Document</p>
                          <p className="text-xs">Click to view</p>
                        </div>
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-white bg-opacity-90 rounded-full p-2">
                        <svg
                          className="h-6 w-6 text-gray-700"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Status badge overlay */}
                    <div className="absolute top-2 right-2">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(artwork.status)} shadow-sm backdrop-blur-sm`}
                      >
                        {artwork.status}
                      </span>
                    </div>
                  </div>
                )}

                {/* Fallback for artworks without URLs */}
                {!artwork.fileUrl && (
                  <div className="h-48 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <svg
                        className="mx-auto h-16 w-16 text-gray-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm mt-2">No preview available</p>
                    </div>
                  </div>
                )}

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {artwork.name}
                      </h3>
                      {!selectedCustomer && artwork.customer && (
                        <p className="text-sm text-blue-600 truncate">
                          {artwork.customer.name} - {artwork.customer.companyName}
                        </p>
                      )}
                    </div>
                    {/* Only show status badge here if no image */}
                    {!artwork.fileUrl && (
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(artwork.status)}`}
                      >
                        {artwork.status}
                      </span>
                    )}
                  </div>

                  {artwork.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">{artwork.description}</p>
                  )}

                  <div className="space-y-2 text-sm text-gray-500">
                    {artwork.fileName && (
                      <div>
                        <span className="font-medium">File:</span> {artwork.fileName}
                      </div>
                    )}
                    {artwork.fileUrl && (
                      <div>
                        <span className="font-medium">URL:</span>
                        <a
                          href={artwork.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 ml-1 truncate inline-block max-w-xs"
                        >
                          {artwork.fileUrl}
                        </a>
                      </div>
                    )}
                    {artwork.dimensions && (
                      <div>
                        <span className="font-medium">Dimensions:</span> {artwork.dimensions.width}{' '}
                        x {artwork.dimensions.height} {artwork.dimensions.unit}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Version:</span> {artwork.version}
                    </div>
                    <div>
                      <span className="font-medium">Created:</span>{' '}
                      {formatDateUK(artwork.createdAt)}
                    </div>
                  </div>

                  <div className="mt-4 flex space-x-2">
                    {artwork.fileUrl && (
                      <button
                        onClick={() => handleViewArtwork(artwork)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </button>
                    )}
                    <button
                      onClick={() => handleEditClick(artwork)}
                      className="text-green-600 hover:text-green-800 text-sm"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {artworks.length === 0 && (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-500">
                  {selectedCustomer ? 'No artworks found for this customer.' : 'No artworks found.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4">Add Artwork</h2>

              <form onSubmit={handleUpload}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Artwork Name *
                  </label>
                  <input
                    type="text"
                    value={uploadForm.name}
                    onChange={e => setUploadForm({ ...uploadForm, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                  <select
                    value={uploadForm.customerId}
                    onChange={e =>
                      setUploadForm({
                        ...uploadForm,
                        customerId: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select customer...</option>
                    {customers && customers.length > 0 ? (
                      customers.map(customer => (
                        <option key={customer._id} value={customer._id}>
                          {customer.name} - {customer.companyName}
                        </option>
                      ))
                    ) : customersLoading ? (
                      <option disabled>Loading customers...</option>
                    ) : (
                      <option disabled>No customers found</option>
                    )}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={uploadForm.description}
                    onChange={e =>
                      setUploadForm({
                        ...uploadForm,
                        description: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    rows="3"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">File URL</label>
                  <input
                    type="url"
                    value={uploadForm.fileUrl}
                    onChange={e =>
                      setUploadForm({
                        ...uploadForm,
                        fileUrl: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="https://example.com/artwork.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the URL where the artwork file is hosted
                  </p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    File Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={uploadForm.fileName}
                    onChange={e =>
                      setUploadForm({
                        ...uploadForm,
                        fileName: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="artwork.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Display name for the file (optional)</p>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    Create
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowUploadModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && editingArtwork && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4">Edit Artwork</h2>

              <form onSubmit={handleEditSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Artwork Name *
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={e =>
                      setEditForm({
                        ...editForm,
                        description: e.target.value,
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    rows="3"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">File URL</label>
                  <input
                    type="url"
                    value={editForm.fileUrl}
                    onChange={e => setEditForm({ ...editForm, fileUrl: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="https://example.com/artwork.jpg"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">File Name</label>
                  <input
                    type="text"
                    value={editForm.fileName}
                    onChange={e => setEditForm({ ...editForm, fileName: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    placeholder="artwork.jpg"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                  >
                    Update
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingArtwork(null);
                      setEditForm({
                        name: '',
                        description: '',
                        fileUrl: '',
                        fileName: '',
                      });
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Artworks;
